import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, ScrollText, Scale, GitBranch, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TIERS, PRODUCTS, productImage, BRAND_IMAGES } from "@/data/catalogue";
import { TierBadge } from "@/components/TierBadge";
import { Reveal } from "@/components/Reveal";

const HERO =
  "https://images.unsplash.com/photo-1746470427686-4c3551f3d689?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const principles = [
  { icon: Scale, title: "Verified authority", body: "Who holds the right to decide is documented and evidenced — never assumed." },
  { icon: ScrollText, title: "Consent & ICIP", body: "Free, Prior and Informed Consent and cultural rights are recorded, honoured and reversible." },
  { icon: ShieldCheck, title: "Deny-by-default", body: "Sensitive access is closed until authority permits it, with human review preserved." },
  { icon: GitBranch, title: "Sovereign exit", body: "Data and control can always be returned cleanly to their rightful custodians." },
];

export default function Home() {
  const featured = PRODUCTS.filter((p) =>
    ["governance-readiness-assessment", "indigenous-data-sovereignty-baseline-review", "big-kh-002-first-nations-governance-architecture-pack"].includes(p.slug)
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[hsl(222_47%_7%)]">
        <img src={HERO} alt="Abstract governance data network" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[hsl(222_47%_7%)]/75" />
        {/* animated data-grid */}
        <div className="pointer-events-none absolute inset-0 hero-grid" aria-hidden="true" />
        {/* pulsing nodes at grid intersections */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <span className="hero-node absolute left-[8%] top-[28%] h-1.5 w-1.5 bg-[hsl(217_91%_62%)]" style={{ animationDelay: "0s" }} />
          <span className="hero-node absolute left-[22%] top-[62%] h-1.5 w-1.5 bg-[hsl(199_89%_60%)]" style={{ animationDelay: "1.1s" }} />
          <span className="hero-node absolute left-[70%] top-[22%] h-1.5 w-1.5 bg-[hsl(217_91%_62%)]" style={{ animationDelay: "0.6s" }} />
          <span className="hero-node absolute left-[86%] top-[54%] h-1.5 w-1.5 bg-[hsl(217_91%_62%)]" style={{ animationDelay: "1.8s" }} />
          <span className="hero-node absolute left-[54%] top-[74%] h-1.5 w-1.5 bg-[hsl(199_89%_60%)]" style={{ animationDelay: "2.4s" }} />
        </div>
        {/* diagonal light sweep */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="hero-sweep absolute inset-y-[-20%] left-0 h-[140%]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:py-28 lg:px-8 lg:py-40">
          <div className="max-w-3xl animate-fade-up">
            <div className="mb-6 text-xs uppercase tracking-widest-xs text-slate-400">
              BIGTEK — the technology arm of Bigibila
            </div>
            <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Governance-first technology for institutions that carry real responsibility.
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300">
              Readiness, authority, consent, evidence and controlled access — built for government,
              enterprise, Nations and community-controlled organisations. Not a brochure. A catalogue
              of engagements, licences and pilots you can commission today.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                data-testid="hero-cta-catalogue"
                className="w-full rounded-sm bg-white py-6 text-base text-slate-900 hover:bg-slate-200 sm:w-auto sm:px-8"
              >
                <Link to="/catalogue">
                  Browse the catalogue <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
                </Link>
              </Button>
              <Button
                asChild
                data-testid="hero-cta-selfcheck"
                variant="outline"
                className="w-full rounded-sm border-slate-500 bg-transparent py-6 text-base text-white hover:bg-white/10 hover:text-white sm:w-auto sm:px-8"
              >
                <Link to="/readiness-check">Take the Readiness Self-Check</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-border sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0">
          {principles.map((p) => (
            <div key={p.title} className="p-8 lg:p-10">
              <p.icon className="h-7 w-7 text-[hsl(var(--brand))]" strokeWidth={1.5} />
              <h3 className="mt-5 font-display text-lg font-medium">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tiers */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">The catalogue</div>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Five status tiers, one governance model.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Products are organised by how you can engage them today — from available services through
            to controlled licences, paid pilots and roadmap concepts.
          </p>
        </div>

        <div className="mt-12 border border-border">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 70}>
            <Link
              to={`/catalogue?tier=${t.id}`}
              data-testid={`tier-row-${t.id}`}
              className={`group flex flex-col gap-4 p-6 transition-colors duration-200 hover:bg-secondary sm:flex-row sm:items-center sm:justify-between lg:p-8 ${
                i > 0 ? "border-t border-border" : ""
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <TierBadge tierId={t.id} />
                  <span className="text-xs text-muted-foreground">
                    {PRODUCTS.filter((p) => p.tier === t.id).length} products
                  </span>
                </div>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t.description}</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-foreground" strokeWidth={1.5} />
            </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Governance Model teaser */}
      <section className="border-y border-border bg-[hsl(222_47%_7%)]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <Reveal>
            <div className="relative overflow-hidden border border-border bg-[hsl(222_47%_5%)]">
              <img
                src={BRAND_IMAGES.sphere}
                alt="The Bigibila Spiny Sphere — Indigenous data governance architecture"
                data-testid="home-sphere-image"
                className="mx-auto w-full max-w-xl object-contain"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5" />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div>
              <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
                The governance model
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                The Bigibila Spiny Sphere™
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Every product operationalises one living architecture — concentric rings of Lore,
                Governance, Community, Permissions, Metadata and Systems protecting a core of Sacred
                Knowledge, with external spines that connect, enforce and uphold it.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Lore", "Governance", "Community", "Permissions", "Metadata", "Systems", "FPIC", "ICIP", "IDS"].map(
                  (chip) => (
                    <span
                      key={chip}
                      className="border border-border bg-secondary/50 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
                    >
                      {chip}
                    </span>
                  )
                )}
              </div>
              <Button
                asChild
                data-testid="home-governance-model-cta"
                className="mt-8 rounded-sm bg-[hsl(var(--brand))] py-6 text-white transition-shadow duration-200 hover:bg-[hsl(217_91%_53%)] hover:glow-primary sm:px-8"
              >
                <Link to="/governance-model">
                  Explore the governance model <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
                </Link>
              </Button>
              <p className="mt-4 font-mono text-xs text-slate-500">
                Bigibila does not store knowledge. It honours relationships.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Featured */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Available now</h2>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            Fixed-scope engagements you can commission today, with published indicative pricing.
          </p>
          <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-3">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 90} className="h-full">
              <Link
                to={`/catalogue/${p.slug}`}
                data-testid={`featured-${p.slug}`}
                className="group flex h-full flex-col bg-background p-8 transition-colors duration-200 hover:bg-secondary"
              >
                <div className="-mx-8 -mt-8 mb-6 h-40 overflow-hidden border-b border-border bg-[hsl(222_47%_7%)]">
                  <img
                    src={productImage(p.slug)}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover opacity-85 transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <TierBadge tierId={p.tier} className="self-start" />
                <h3 className="mt-6 font-display text-xl font-medium leading-tight">
                  {p.name}
                  {p.tm && <sup className="ml-0.5 text-xs">™</sup>}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.tagline}</p>
                <div className="mt-6 border-t border-border pt-4">
                  <div className="font-mono text-sm font-medium text-[hsl(var(--brand))]">{p.price}</div>
                </div>
              </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Executive briefing / booking */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <div className="grid items-center gap-10 border border-border bg-[hsl(222_47%_7%)] p-10 text-white lg:grid-cols-2 lg:p-16">
          <div>
            <div className="text-xs uppercase tracking-widest-xs text-slate-400">Executive briefing</div>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">
              Book a 90-minute executive briefing.
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300">
              A focused session for boards and executives on governance readiness, data sovereignty
              obligations and where BIGTEK's catalogue fits your program.
            </p>
            <div className="mt-6 font-mono text-sm text-slate-400">From $9,500 + GST · indicative</div>
          </div>
          <div className="lg:justify-self-end">
            <Button
              asChild
              data-testid="booking-cta"
              className="w-full rounded-sm bg-[hsl(var(--brand))] py-6 text-base text-white hover:bg-[hsl(217_91%_53%)] sm:w-auto sm:px-10"
            >
              <Link to="/book-briefing">
                <Calendar className="mr-2 h-4 w-4" strokeWidth={1.75} /> Book a briefing
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
